import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil, X } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type Service } from "@/lib/api"
import { AppIcon } from "@/lib/icon"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { ConfirmDelete } from "@/components/admin/confirm-delete"

export const Route = createFileRoute("/admin/_authed/services")({
  component: ServicesPage,
})

type FormState = {
  icon: string
  title: string
  description: string
  order: number
  is_active: boolean
  features: string[]
  cta_text: string
  cta_link: string
}
const empty: FormState = {
  icon: "sparkles",
  title: "",
  description: "",
  order: 0,
  is_active: true,
  features: [],
  cta_text: "Learn More",
  cta_link: "/contact",
}

function ServicesPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<Service[]>({
    queryKey: ["admin", "services"],
    queryFn: async () => (await api.get<ApiEnvelope<Service[]>>("/admin/services")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Service | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "services"] })
    queryClient.invalidateQueries({ queryKey: ["services"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        features: form.features.map((f) => f.trim()).filter((f) => f.length > 0),
        cta_text: form.cta_text.trim() || null,
        cta_link: form.cta_link.trim() || null,
      }
      if (editing) await api.put(`/admin/services/${editing.id}`, payload)
      else await api.post("/admin/services", payload)
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Service updated." : "Service created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/services/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Service deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: Service) => {
    setEditing(item)
    setForm({
      icon: item.icon,
      title: item.title,
      description: item.description,
      order: item.order,
      is_active: item.is_active,
      features: item.features ?? [],
      cta_text: item.cta_text ?? "",
      cta_link: item.cta_link ?? "",
    })
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Services</h1>
          <p className="mt-1 text-sm text-muted-foreground">Capabilities shown on the public services page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New service
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit service" : "New service"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    aria-invalid={!!fieldErrors.title}
                  />
                  <FieldError message={fieldErrors.title} />
                </div>
                <div>
                  <Label htmlFor="icon">Icon</Label>
                  <Input
                    id="icon"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    aria-invalid={!!fieldErrors.icon}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">lucide.dev name, kebab-case (e.g. brain-circuit).</p>
                  <FieldError message={fieldErrors.icon} />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  aria-invalid={!!fieldErrors.description}
                />
                <FieldError message={fieldErrors.description} />
              </div>
              <div>
                <Label>Feature checklist</Label>
                <div className="mt-2 space-y-2">
                  {form.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={feature}
                        placeholder="e.g. Priority Support"
                        onChange={(e) => {
                          const features = [...form.features]
                          features[i] = e.target.value
                          setForm({ ...form, features })
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setForm({ ...form, features: [...form.features, ""] })}
                  disabled={form.features.length >= 8}
                >
                  <Plus className="h-4 w-4" /> Add feature
                </Button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="cta_text">CTA text</Label>
                  <Input id="cta_text" value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} placeholder="Learn More" />
                </div>
                <div>
                  <Label htmlFor="cta_link">CTA link</Label>
                  <Input id="cta_link" value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} placeholder="/contact" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-3 pb-2">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="is_active" />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="text-sm text-destructive">Couldn't load services.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data ?? []).map((s) => {
              return (
                <div key={s.id} className="rounded-2xl border border-border bg-card p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-cream">
                      <AppIcon name={s.icon} className="h-5 w-5" />
                    </div>
                    <Badge variant={s.is_active ? "default" : "secondary"}>{s.is_active ? "Active" : "Hidden"}</Badge>
                  </div>
                  <h3 className="mt-4 font-semibold">{s.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{s.description}</p>
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(s)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete itemLabel={s.title} onConfirm={() => remove.mutate(s.id)} />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
